import {
  ConfirmCancelDialog,
  MuiTable,
  Spacer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  useToast,
} from '@color/continuum';
import { useState } from 'react';

import { useImportConfirmationResultsMutation } from '../api';
import { FileDropzone } from '../FileDropzone';

const ACCEPTED_EXTENSIONS = '.txt,.tsv,.xlsx';

interface ImportConfirmationResultsDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const ImportConfirmationResultsDialog = ({
  onClose,
  onSuccess,
}: ImportConfirmationResultsDialogProps) => {
  const [file, setFile] = useState<File | null>(null);
  const { showToast } = useToast();
  const { mutateAsync: importResults, isPending } = useImportConfirmationResultsMutation();

  const handleSubmit = async () => {
    if (!file) return;
    try {
      await importResults(file);
      showToast('Confirmation results imported.', { severity: 'success' });
      onSuccess();
    } catch {
      // Error toast shown by the mutation hook.
    }
  };

  return (
    <ConfirmCancelDialog
      title="Import Confirmation Result"
      confirmButtonText="Import"
      confirmButtonProps={{ disabled: !file || isPending }}
      onAccept={handleSubmit}
      onCancel={onClose}
      dialogIsOpen
      maxWidth="sm"
      fullWidth
    >
      <Text>
        The file must be tab-delimited (.txt or .tsv) or an Excel spreadsheet (.xlsx) and must
        include the following columns:
      </Text>
      <Spacer variant="small" />
      <MuiTable size="small">
        <TableHead>
          <TableRow>
            <TableCell>Column</TableCell>
            <TableCell>Description</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Accession #</TableCell>
            <TableCell>Color confirmation ID</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Lab #</TableCell>
            <TableCell>Vendor ID</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Present</TableCell>
            <TableCell>Outcome of confirmation (yes / no / failed)</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Comments</TableCell>
            <TableCell>Comments from vendor</TableCell>
          </TableRow>
        </TableBody>
      </MuiTable>
      <Spacer variant="medium" />
      <FileDropzone
        files={file ? [file] : []}
        onFilesChange={(files) => setFile(files[0] ?? null)}
        acceptedExtensions={ACCEPTED_EXTENSIONS}
      />
    </ConfirmCancelDialog>
  );
};
